// src/components/admin/flows/ExecutionDetail.js
import React, { useEffect, useState } from 'react';
import {
    ArrowLeft, CheckCircle, XCircle, Clock, AlertTriangle,
    RefreshCw, RotateCcw, XOctagon, ChevronDown, ChevronRight
} from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useFlowAutomation } from '../../../contexts/FlowAutomationContext';
import flowAutomationService from '../../../services/flowAutomation.service';
import StatusBadge from './components/StatusBadge';
import StepIcon from './components/StepIcon';
import SLAIndicator from './components/SLAIndicator';

// Format step output as human-readable text based on the step type and action
const humanizeStepOutput = (output, stepType) => {
    if (!output) return null;
    const data = typeof output === 'string' ? (() => { try { return JSON.parse(output); } catch { return { message: output }; } })() : output;

    // Trigger step
    if (stepType === 'trigger' && data.event) {
        const eventLabel = String(data.event).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return [{ label: 'Trigger fired', value: eventLabel }];
    }

    // Action — different shapes per action_type
    if (stepType === 'action' || data.type) {
        const t = data.type;
        if (t === 'send_notification') {
            return [
                { label: 'Action', value: 'Sent notification' },
                data.title && { label: 'Title', value: data.title },
                data.message && { label: 'Message', value: data.message },
                { label: 'Status', value: data.sent ? '✓ Delivered' : '✗ Failed' },
                data.error && { label: 'Error', value: data.error, isError: true },
            ].filter(Boolean);
        }
        if (t === 'send_email') {
            return [
                { label: 'Action', value: 'Sent email' },
                data.to && { label: 'To', value: data.to },
                data.subject && { label: 'Subject', value: data.subject },
                { label: 'Status', value: data.sent ? '✓ Sent' : '✗ Failed' },
                data.message_id && { label: 'Message ID', value: data.message_id },
                data.error && { label: 'Error', value: data.error, isError: true },
            ].filter(Boolean);
        }
        if (t === 'create_task') {
            return [
                { label: 'Action', value: 'Created task' },
                data.title && { label: 'Task', value: data.title },
                { label: 'Status', value: data.created ? '✓ Created' : '✗ Failed' },
                data.error && { label: 'Error', value: data.error, isError: true },
            ].filter(Boolean);
        }
        if (t === 'create_document') {
            const humanType = (data.document_type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            return [
                { label: 'Action', value: 'Generated document' },
                humanType && { label: 'Document', value: humanType },
                data.document_name && { label: 'File', value: data.document_name },
                data.file_size && { label: 'Size', value: `${Math.round(data.file_size / 1024)} KB` },
                { label: 'Status', value: data.created ? '✓ Generated' : '✗ Failed' },
                data.error && { label: 'Error', value: data.error, isError: true },
            ].filter(Boolean);
        }
        if (t === 'process_payroll') {
            const period = data.month ? `${data.month}/${data.year}` : '';
            if (data.skipped) {
                const reason = data.skipped === 'period_locked' ? 'Period is locked' : 'Already processed';
                return [
                    { label: 'Action', value: 'Process payroll' },
                    period && { label: 'Period', value: period },
                    { label: 'Status', value: `⏭ Skipped — ${reason}` },
                ].filter(Boolean);
            }
            return [
                { label: 'Action', value: 'Processed payroll' },
                period && { label: 'Period', value: period },
                data.employees !== undefined && { label: 'Employees', value: String(data.employees) },
                data.total_net !== undefined && { label: 'Net payable', value: `₹${Number(data.total_net).toLocaleString('en-IN')}` },
                { label: 'Status', value: data.processed ? '✓ Completed' : '✗ Failed' },
                data.error && { label: 'Error', value: data.error, isError: true },
            ].filter(Boolean);
        }
        if (t === 'generate_payslips') {
            return [
                { label: 'Action', value: 'Generated payslips' },
                data.period && { label: 'Period', value: data.period },
                { label: 'Generated', value: String(data.generated ?? 0) },
                data.skipped_held > 0 && { label: 'Held (skipped)', value: String(data.skipped_held) },
                data.notified > 0 && { label: 'Employees notified', value: String(data.notified) },
                data.failed?.length > 0 && { label: 'Failed', value: data.failed.map(f => f.employee_code).join(', '), isError: true },
                data.error && { label: 'Error', value: data.error, isError: true },
            ].filter(Boolean);
        }
    }

    // Condition step
    if (stepType === 'condition') {
        return [
            { label: 'Condition', value: data.condition || 'Evaluated condition' },
            { label: 'Result', value: data.result ? '✓ Matched (Yes branch)' : '✗ Did not match (No branch)' },
        ];
    }

    // Approval step
    if (stepType === 'approval') {
        if (data.approved !== undefined) {
            // Final resolution
            return [
                { label: 'Decision', value: data.approved ? '✓ Approved' : '✗ Rejected' },
                data.approved_by && { label: 'By', value: data.approved_by },
                data.rejected_by && { label: 'By', value: data.rejected_by },
                data.comments && { label: 'Comment', value: data.comments },
            ].filter(Boolean);
        }
        // Still pending
        return [
            { label: 'Status', value: 'Waiting for approval' },
            data.approver_label && { label: 'Approver', value: data.approver_label },
            data.auto_approve_hours && { label: 'Auto-approve after', value: `${data.auto_approve_hours} hours` },
        ].filter(Boolean);
    }

    // Delay step
    if (stepType === 'delay') {
        return [
            { label: 'Action', value: 'Waited' },
            data.hours && { label: 'Duration', value: `${data.hours} hour(s)` },
        ].filter(Boolean);
    }

    // End step
    if (stepType === 'end') {
        return [{ label: 'Status', value: 'Workflow completed' }];
    }

    // Fallback: show known fields if any
    const known = [];
    if (data.message) known.push({ label: 'Message', value: data.message });
    if (data.status) known.push({ label: 'Status', value: data.status });
    return known.length > 0 ? known : null;
};

const ExecutionDetail = ({ executionId, onBack }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [execution, setExecution] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedStep, setExpandedStep] = useState(null);

    useEffect(() => {
        const loadExecution = async () => {
            setLoading(true);
            const result = await flowAutomationService.getExecution(executionId);
            if (result.success !== false) {
                setExecution(result.execution);
            }
            setLoading(false);
        };
        if (executionId) loadExecution();
    }, [executionId]);

    const handleRetry = async () => {
        const result = await flowAutomationService.retryExecution(executionId);
        if (result.success !== false) {
            setExecution(prev => ({ ...prev, status: 'running' }));
        }
    };

    const handleCancel = async () => {
        const result = await flowAutomationService.cancelExecution(executionId);
        if (result.success !== false) {
            setExecution(prev => ({ ...prev, status: 'cancelled' }));
        }
    };

    const getStepStatusIcon = (status) => {
        switch (status) {
            case 'completed': return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'failed': return <XCircle className="w-5 h-5 text-red-500" />;
            case 'running': return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
            case 'skipped': return <AlertTriangle className="w-5 h-5 text-gray-400" />;
            default: return <Clock className="w-5 h-5 text-gray-400" />;
        }
    };

    const formatTime = (ts) => ts ? new Date(ts).toLocaleString() : '—';

    const formatDuration = (start, end) => {
        if (!start) return '—';
        const s = new Date(start);
        const e = end ? new Date(end) : new Date();
        const ms = e - s;
        const secs = Math.floor(ms / 1000);
        const mins = Math.floor(secs / 60);
        const hrs = Math.floor(mins / 60);
        if (hrs > 0) return `${hrs}h ${mins % 60}m`;
        if (mins > 0) return `${mins}m ${secs % 60}s`;
        return `${secs}s`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            </div>
        );
    }

    if (!execution) {
        return (
            <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Execution not found
            </div>
        );
    }

    const stepResults = execution.step_results || [];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={onBack}
                        className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Execution Detail
                        </h2>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {execution.workflow_name || 'Workflow'} — {execution.id}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {execution.status === 'failed' && (
                        <button
                            onClick={handleRetry}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-blue-500 text-white hover:bg-blue-600"
                        >
                            <RotateCcw className="w-4 h-4" />
                            Retry
                        </button>
                    )}
                    {execution.status === 'running' && (
                        <button
                            onClick={handleCancel}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-red-500 text-white hover:bg-red-600"
                        >
                            <XOctagon className="w-4 h-4" />
                            Cancel
                        </button>
                    )}
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</p>
                    <StatusBadge status={execution.status} />
                </div>
                <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Started</p>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatTime(execution.started_at)}
                    </p>
                </div>
                <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Duration</p>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatDuration(execution.started_at, execution.completed_at)}
                    </p>
                </div>
                <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Triggered By</p>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {execution.triggered_by || 'System'}
                    </p>
                </div>
            </div>

            {/* SLA */}
            {execution.status === 'running' && execution.sla_deadline && (
                <SLAIndicator deadline={execution.sla_deadline} />
            )}

            {/* Step-by-Step Timeline */}
            <div className={`rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className={`p-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Execution Steps ({stepResults.length})
                    </h3>
                </div>

                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {stepResults.length === 0 ? (
                        <div className={`p-8 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            No step results available
                        </div>
                    ) : (
                        stepResults.map((stepResult, index) => {
                            const isExpanded = expandedStep === stepResult.step_id;
                            return (
                                <div key={stepResult.step_id || index}>
                                    <button
                                        onClick={() => setExpandedStep(isExpanded ? null : stepResult.step_id)}
                                        className={`w-full flex items-center gap-4 p-4 text-left transition-colors ${
                                            isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        {/* Timeline connector */}
                                        <div className="relative flex flex-col items-center">
                                            {getStepStatusIcon(stepResult.status)}
                                            {index < stepResults.length - 1 && (
                                                <div className={`absolute top-7 w-0.5 h-6 ${
                                                    stepResult.status === 'completed' ? 'bg-green-300 dark:bg-green-700' : 'bg-gray-200 dark:bg-gray-700'
                                                }`} />
                                            )}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <StepIcon type={stepResult.step_type || 'action'} size="xs" />
                                                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                    {stepResult.step_label || `Step ${index + 1}`}
                                                </p>
                                                <StatusBadge status={stepResult.status} />
                                            </div>
                                            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                {formatTime(stepResult.started_at)}
                                                {stepResult.completed_at && ` — ${formatDuration(stepResult.started_at, stepResult.completed_at)}`}
                                            </p>
                                        </div>

                                        {isExpanded ? (
                                            <ChevronDown className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                        ) : (
                                            <ChevronRight className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                        )}
                                    </button>

                                    {/* Expanded details */}
                                    {isExpanded && (
                                        <div className={`px-4 pb-4 ml-12 space-y-2`}>
                                            {(() => {
                                                const rows = humanizeStepOutput(stepResult.output, stepResult.step_type);
                                                if (rows && rows.length > 0) {
                                                    return (
                                                        <div className={`text-xs rounded-lg p-3 space-y-1.5 ${
                                                            isDark ? 'bg-gray-900/50' : 'bg-gray-50'
                                                        }`}>
                                                            {rows.map((row, i) => (
                                                                <div key={i} className="flex gap-2">
                                                                    <span className={`font-medium min-w-[90px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                        {row.label}:
                                                                    </span>
                                                                    <span className={`${row.isError ? 'text-red-500' : isDark ? 'text-gray-200' : 'text-gray-800'} break-words`}>
                                                                        {row.value}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                            {stepResult.error && (
                                                <div className={`text-xs rounded-lg p-3 flex gap-2 ${
                                                    isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'
                                                }`}>
                                                    <span className="font-medium">Error:</span>
                                                    <span>{stepResult.error}</span>
                                                </div>
                                            )}
                                            {!stepResult.output && !stepResult.error && (
                                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    No additional details available
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Error Details */}
            {execution.error && (
                <div className={`p-4 rounded-xl border ${isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
                    <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">Execution Error</p>
                    <p className="text-sm text-red-600 dark:text-red-300">{execution.error}</p>
                </div>
            )}
        </div>
    );
};

export default ExecutionDetail;
